import { z } from 'zod'
import { AchievementLevel } from '../types/student'
import { getSchoolYearBounds } from '../constants/schoolYears'
import { isValidUsPhone, US_PHONE_ERROR } from '../lib/phone'
import { httpUrlSchema } from '../lib/url'

const { min: minSchoolYear, max: maxSchoolYear } = getSchoolYearBounds()

// ─── Parent/Guardian (nested inside personalInfo) ───────────────────────────

export const parentGuardianSchema = z.object({
  firstName: z.string().min(1, 'Parent/guardian first name is required').max(50),
  lastName: z.string().min(1, 'Parent/guardian last name is required').max(50),
  email: z.string().email('Invalid email address'),
  phoneNumber: z.string().refine(isValidUsPhone, US_PHONE_ERROR),
})

// ─── Personal Info ──────────────────────────────────────────────────────────

export const personalInfoSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email address'),
  mobilePhone: z.string().refine(isValidUsPhone, US_PHONE_ERROR),
  country: z.string().min(1, 'Country/region is required'),
  address: z.object({
    line1: z.string().min(1, 'Address line 1 is required'),
    line2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    zipCode: z.string().regex(/^[\d\w\s-]+$/, 'Invalid ZIP code'),
    state: z.string().min(2, 'State is required').max(2),
  }),
  dateOfBirth: z.coerce.date(),
  genderIdentity: z
    .union([
      z.literal('male'),
      z.literal('female'),
      z.literal('non-binary'),
      z.literal('other'),
      z.literal('prefer-not-to-say'),
    ])
    .describe('Self-described gender identity')
    .default('prefer-not-to-say'),
  genderIdentityDetails: z
    .string()
    .max(100, 'Please keep additional details under 100 characters')
    .optional(),
  competitionTeam: z.union([z.literal('men'), z.literal('women')]),
  profilePhoto: httpUrlSchema.optional(),
  bio: z.string().max(500).optional(),
  parentGuardian: parentGuardianSchema,
})

// ─── Academic Info ──────────────────────────────────────────────────────────

export const academicInfoSchema = z.object({
  highSchool: z.string().min(1, 'High school is required'),
  graduationYear: z.number().int().min(minSchoolYear).max(maxSchoolYear),
  gpa: z.number().min(0).max(4.0),
  satScore: z.number().int().min(400).max(1600).optional(),
  actScore: z.number().int().min(1).max(36).optional(),
  academicAchievements: z.array(z.string()).optional(),
  intendedMajor: z.string().optional(),
  resumeUrl: httpUrlSchema.optional(),
})

// ─── Athletic Info ──────────────────────────────────────────────────────────

export const achievementSchema = z.object({
  id: z.string(),
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  date: z.coerce.date(),
  level: z.nativeEnum(AchievementLevel),
  eventId: z.string().optional(),
})

export const coachReferenceSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Name is required'),
  title: z.string().min(1, 'Title is required'),
  school: z.string().min(1, 'School is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().refine(isValidUsPhone, US_PHONE_ERROR),
  relationship: z.string().min(1, 'Relationship is required'),
})

export const athleticInfoSchema = z.object({
  ncaaId: z.string().optional(),
  height: z.object({
    feet: z.number().int().min(3).max(8),
    inches: z.number().int().min(0).max(11),
  }),
  position: z.string().optional(),
  primaryEvents: z.array(z.string()).min(1, 'At least one primary event is required'),
  recruitingVideoLink: httpUrlSchema.optional(),
  clubTeam: z.string().optional(),
  clubPosition: z.string().optional(),
  plannedFirstYearOfCollege: z.number().int().min(minSchoolYear).max(maxSchoolYear),
  achievements: z.array(achievementSchema).optional(),
  videoLinks: z.array(httpUrlSchema).optional(),
  coachReferences: z.array(coachReferenceSchema).optional(),
  trainingLogs: z
    .array(
      z.object({
        id: z.string(),
        date: z.coerce.date(),
        workout: z.string().min(1, 'Workout description is required'),
        duration: z.number().positive().optional(),
        notes: z.string().optional(),
      })
    )
    .optional(),
})

// ─── Personal Record (separate table) ───────────────────────────────────────

export const personalRecordSchema = z.object({
  eventId: z.string().min(1, 'Event is required'),
  mark: z.string().min(1, 'Mark is required'),
  value: z.number().positive('Value must be positive'),
  date: z.coerce.date().optional(),
  venue: z.string().optional(),
  isVerified: z.boolean().default(false),
  isOutdoor: z.boolean().optional(),
})

export const createPersonalRecordSchema = personalRecordSchema

export const updatePersonalRecordSchema = personalRecordSchema.partial()

/** User-facing copy for PR validation (each primary event needs a best mark + numeric value). */
export const PERSONAL_RECORDS_COVERAGE_MESSAGE =
  'Add a best mark (PR) for each primary event you selected.'

const isValidPrEntry = (record: { eventId?: string; mark?: string; value?: number }) => {
  const hasMark = Boolean(record.mark?.trim())
  const v = record.value
  return Boolean(
    record.eventId?.trim() && hasMark && typeof v === 'number' && Number.isFinite(v) && v > 0
  )
}

/** True when every `primaryEvents` id has a valid personal record in `records`. */
export function arePersonalRecordsCompleteForPrimaryEvents(
  primaryEvents: string[] | undefined,
  records: Array<{ eventId?: string; mark?: string; value?: number }> | undefined
): boolean {
  if (!primaryEvents || primaryEvents.length === 0) return false
  if (!records || records.length === 0) return false
  const byId = new Map<string, { eventId?: string; mark?: string; value?: number }>()
  for (const r of records) {
    if (r?.eventId) byId.set(r.eventId, r)
  }
  return primaryEvents.every(id => isValidPrEntry(byId.get(id) ?? {}))
}

const studentProfileObject = z.object({
  personalInfo: personalInfoSchema,
  academicInfo: academicInfoSchema,
  athleticInfo: athleticInfoSchema,
  personalRecords: z.array(personalRecordSchema),
  isPublic: z.boolean().optional(),
})

export const studentProfileSchema = studentProfileObject.superRefine((data, ctx) => {
  if (
    !arePersonalRecordsCompleteForPrimaryEvents(
      data.athleticInfo.primaryEvents,
      data.personalRecords
    )
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['personalRecords'],
      message: PERSONAL_RECORDS_COVERAGE_MESSAGE,
    })
  }
})

export const updateStudentProfileSchema = studentProfileObject
  .partial()
  .superRefine((data, ctx) => {
    if (data.athleticInfo?.primaryEvents === undefined || data.personalRecords === undefined) {
      return
    }
    if (
      !arePersonalRecordsCompleteForPrimaryEvents(
        data.athleticInfo.primaryEvents,
        data.personalRecords
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personalRecords'],
        message: PERSONAL_RECORDS_COVERAGE_MESSAGE,
      })
    }
  })
